import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';
import { ArrowForward } from '@material-ui/icons';
import EstablishmentsTable from 'components/Dashboard/WorldCup/Establishments/EstablishmentsTable';
import Loading from 'components/Basic/Loading';
import EstablishmentsFormModal from 'components/Dashboard/WorldCup/Establishments/EstablishmentsFormModal';
import EstablishmentsFilter from 'components/Dashboard/WorldCup/Establishments/EstablishmentsFilter';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { useLazyGetEstablishmentsListQuery } from 'core/worldCup/establishments/EstablishmentsService';
import { setEstablishmentsData } from 'core/worldCup/establishments/EstablishmentsSlice';

const Establishments: React.FC = () => {
  const [state, setState] = useState({
    isLoading: false,
    isShowDetailModal: false,
    searchText: '',
    sortStatus: '',
    filterStatus: '',
    sortName: '',
    sortId: 'desc',
  });
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const dispatch = useAppDispatch();
  const [getEstablishmentsList] = useLazyGetEstablishmentsListQuery();

  const { list } = useAppSelector(state => state.establishmentsReducer);

  useEffect(() => {
    getInitialData();
    setState({ ...state, searchText: '' });
  }, []);

  useEffect(() => {
    setCurrent(1);
  }, [pageSize]);

  const getInitialData = async () => {
    setState({ ...state, isLoading: true });

    const queryString = buildQueryString({
      search: '',
      page: 1,
      size: pageSize,
      count: true,
      sort_by_id: state.sortId,
      filter: state.filterStatus,
    });

    try {
      const res = await getEstablishmentsList({ queryString }).unwrap();
      dispatch(setEstablishmentsData({ list: res }));
    } catch (error) {
      console.error('Error fetching establishments list:', error);
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  const handleChangePage = async (page: number, size: number, search = '') => {
    setState(prev => ({ ...prev, isLoading: true }));
    setPageSize(size);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: page,
      size: size,
      count: true,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      filter: state.filterStatus,
    });

    try {
      const res = await getEstablishmentsList({ queryString }).unwrap();
      dispatch(setEstablishmentsData({ list: res }));
      setState(prev => ({ ...prev, isLoading: false }));
    } catch (e) {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSearch = async (e: any) => {
    const searchTextValue = e.target.value.trimStart();
    setState({ ...state, searchText: searchTextValue });
    if (e.keyCode === 13) {
      setCurrent(1);

      const queryString = buildQueryString({
        search: e.target.value.trim(),
        page: 1,
        size: pageSize,
        count: true,
        sort_by_status: state.sortStatus,
        sort_by_id: state.sortId,
        sort_by_name: state.sortName,
        filter: state.filterStatus,
      });

      try {
        const res = await getEstablishmentsList({ queryString }).unwrap();
        dispatch(setEstablishmentsData({ list: res }));
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleAvailableFilter = (value: string) => {
    setState({ ...state, filterStatus: value });
  };

  const handleSort = (value: string) => {
    let newState = { ...state };
    if (value[0] === 'I') {
      newState = {
        ...newState,
        sortId: value.substring(1),
        sortName: '',
        sortStatus: '',
      };
    } else if (value[0] === 'N') {
      newState = {
        ...newState,
        sortName: value.substring(1),
        sortId: '',
        sortStatus: '',
      };
    } else if (value[0] === 'S') {
      newState = {
        ...newState,
        sortStatus: value.substring(1),
        sortId: '',
        sortName: '',
      };
    }
    setState(newState);
  };

  const onApplyFilter = async () => {
    setState({ ...state, isLoading: true });
    setCurrent(1);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: 1,
      size: pageSize,
      count: true,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      filter: state.filterStatus,
    });

    try {
      const res = await getEstablishmentsList({ queryString }).unwrap();
      dispatch(setEstablishmentsData({ list: res }));
    } catch (e) {
      console.log(e);
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  return (
    <div className="establishments_wrapper" id="establishments_wrapper" style={{ position: 'relative' }}>
      <Loading visible={state.isLoading} />
      <div className="btn_wrapper" id="new_establishment_btn_wrapper">
        <Button
          className="add_btn"
          onClick={() => setState({ ...state, isShowDetailModal: true })}
          id="new_establishment_btn"
        >
          <FormattedMessage id="Add new establishment" />
          <ArrowForward />
        </Button>
      </div>
      <EstablishmentsFilter
        searchText={state.searchText}
        handleSearch={handleSearch}
        onAvailableFilter={handleAvailableFilter}
        onSort={handleSort}
        onApplyFilter={onApplyFilter}
      />
      <EstablishmentsTable
        establishmentsData={list.data || null}
        onChangePage={(current, size) => handleChangePage(current, size)}
        total={list.meta?.totalCount || 0}
        setCurrent={setCurrent}
        current={current}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
      <EstablishmentsFormModal
        mode="modal"
        isOpenModal={state.isShowDetailModal}
        onCloseModal={() => setState({ ...state, isShowDetailModal: false })}
        onSaveSuccess={() => handleChangePage(current, pageSize)}
        establishment={null}
      />
    </div>
  );
};

Establishments.defaultProps = {};

export default Establishments;

const buildQueryString = ({
  search = '',
  page = 1,
  size = 10,
  count = true,
  sort_by_id = 'desc',
  sort_by_status = '',
  sort_by_name = '',
  filter = '',
}) => {
  const params = [`search=${encodeURIComponent(search)}`, `page=${page}`, `limit=${size}`];

  if (sort_by_status) {
    params.push(`sort=isActive`);
    params.push(`sortDirection=${sort_by_status}`);
  }

  if (sort_by_id) {
    params.push(`sort=id`);
    params.push(`sortDirection=${sort_by_id}`);
  }

  if (sort_by_name) {
    params.push(`sort=name`);
    params.push(`sortDirection=${sort_by_name}`);
  }

  if (filter) {
    params.push(`isActive=${filter}`);
  }

  return params.join('&');
};
